// plan-done-feature-ticket-guards.ts
//
// Dogfood V2.1 lần 2 (09/10, F-1 blocker). A plan worker returned five tickets
// in `proposed_tickets` (the pack's PLAN.tpl.md taught that field) with
// `outcome: done`. Only `proposed_changes` is ever applied, so the commit was
// `no_change`, the plan ticket went `done`, every ticket on disk was done ⇒
// "DAG exhausted", G2 was never requested and the candidate review ran on the
// bootstrap commit until `contract_error` (missing_candidate_diff).
//
// Three checks, all derived from the tickets on disk + journal statuses:
//   R1 — any done attempt whose `proposed_tickets` is non-empty is escalated
//        (the field stays in the schema for v1 wire compatibility, always `[]`);
//   R2 — a `plan` ticket may not finish while the mission has no effective
//        feature ticket;
//   R3 — run-until-idle never enters candidate review without one.
// `planDoneRejection` bundles the done-path's plan checks (06d R2 delegation
// + R2 here) so supervisor-outcome-router-done-path.ts stays under budget.

import type { AutopilotResult } from "./autopilot-result-schema.js";
import { isControllerReservedTicketId } from "./controller-reserved-ticket-ids.js";
import { BOOTSTRAP_TICKET_KINDS } from "./feature-dispatch-gate-guard.js";
import { RETIRED_TICKET_STATUSES } from "./g2-plan-readiness.js";
import type { Ticket, TicketKind } from "./schemas/ticket-schema.js";
import { findPlanAcceptanceDelegatingToWorker } from "./worker-decisions-g0-delegation-claim-guard.js";

/** Kinds that are not product work: the bootstrap seeds and read-only redirect decisions. */
const NON_FEATURE_TICKET_KINDS: readonly TicketKind[] = [...BOOTSTRAP_TICKET_KINDS, "decision"];

/**
 * Product work for R2/R3 (review/fix/deploy count too). NOT the 06e R3
 * supersede trigger — that is ACCEPTANCE_REQUIRED_KINDS (B2: two named sets).
 */
export function isFeatureTicketKind(kind: unknown): boolean {
  return typeof kind === "string" && !NON_FEATURE_TICKET_KINDS.includes(kind as TicketKind);
}

/** Feature tickets still in the plan (not cancelled/superseded), controller review sessions excluded. */
export function countEffectiveFeatureTickets(
  tickets: readonly Ticket[],
  ticketStatus: Record<string, string>,
): number {
  return tickets.filter(
    (t) =>
      isFeatureTicketKind(t.kind) &&
      !isControllerReservedTicketId(t.id) &&
      !RETIRED_TICKET_STATUSES.has(ticketStatus[t.id] ?? "todo"),
  ).length;
}

/** R1: reason to reject a done attempt, or undefined when `proposed_tickets` is empty. */
export function proposedTicketsRejectionReason(
  ticketId: string,
  proposedTickets: readonly unknown[],
): string | undefined {
  if (proposedTickets.length === 0) return undefined;
  return (
    `ticket ${ticketId} trả ${proposedTickets.length} mục trong proposed_tickets — field này không ` +
    "bao giờ được áp (luôn để []); ticket mới phải đi qua proposed_changes (op create + new_ticket). " +
    "Kết luận done bị giữ lại, cần người xem rồi chạy lại ticket."
  );
}

/**
 * Feature tickets the graph WOULD hold once `proposedChanges` applied: on disk
 * minus cancel/supersede targets, plus creates whose `content.kind` is feature
 * work. Computed BEFORE the apply (review 261010 M2) so a rejected plan leaves
 * no cancel in the journal and no orphan ticket on disk. A create the applier
 * would reject is counted here, then the apply rejects the whole result anyway.
 */
export function projectedFeatureTicketCount(
  tickets: readonly Ticket[],
  ticketStatus: Record<string, string>,
  proposedChanges: AutopilotResult["proposed_changes"],
): number {
  const removed = new Set(
    proposedChanges.filter((c) => c.op === "cancel" || c.op === "supersede").map((c) => c.target),
  );
  const created = proposedChanges.filter(
    (c) => c.op === "create" && isFeatureTicketKind((c.content as { kind?: unknown })?.kind),
  ).length;
  return (
    countEffectiveFeatureTickets(
      tickets.filter((t) => !removed.has(t.id)),
      ticketStatus,
    ) + created
  );
}

/** R2: reason to reject a finished `plan` ticket that would leave no feature work, else undefined. */
export function planDoneWithoutFeatureTicketsReason(
  ticketId: string,
  ticketKind: string,
  tickets: readonly Ticket[],
  ticketStatus: Record<string, string>,
  proposedChanges: AutopilotResult["proposed_changes"],
): string | undefined {
  if (ticketKind !== "plan") return undefined;
  if (projectedFeatureTicketCount(tickets, ticketStatus, proposedChanges) > 0) return undefined;
  return (
    `ticket plan ${ticketId} báo done nhưng mission không có ticket feature nào — plan không sinh ` +
    "việc (ticket mới phải nằm trong proposed_changes). G2 không có gì để duyệt; cần người xem."
  );
}

/**
 * The done-path's plan checks, in their historical order: a plan acceptance
 * delegating an open decision to the worker (06d R2), then R2 above. Returns
 * the receipt outcome to journal + the escalation reason, or undefined.
 */
export function planDoneRejection(
  input: {
    ticketId: string;
    ticketType: string;
    ticketStatus: Record<string, string>;
    proposedChanges: AutopilotResult["proposed_changes"];
    secretValues?: string[];
  },
  repoTickets: readonly Ticket[],
): { receipt: string; reason: string } | undefined {
  if (input.ticketType !== "plan") return undefined;
  const delegating = findPlanAcceptanceDelegatingToWorker(input);
  if (delegating !== undefined) {
    return { receipt: "plan_delegates_open_decision_to_worker_rejected", reason: delegating };
  }
  const reason = planDoneWithoutFeatureTicketsReason(
    input.ticketId,
    input.ticketType,
    repoTickets,
    input.ticketStatus,
    input.proposedChanges,
  );
  return reason === undefined
    ? undefined
    : { receipt: "plan_done_without_feature_tickets", reason };
}

/** R3: halt reason when every ticket is settled but none of them is feature work, else undefined. */
export function noFeatureTicketHaltReason(
  tickets: readonly Ticket[],
  ticketStatus: Record<string, string>,
): string | undefined {
  if (tickets.length === 0 || countEffectiveFeatureTickets(tickets, ticketStatus) > 0) {
    return undefined;
  }
  return (
    "DAG đã hết nhưng không có ticket feature nào (chỉ survey/plan/decision) — không có candidate " +
    'để review. Chỉ đạo lại plan: `avatar autopilot redirect --text "<mô tả>"` hoặc `avatar autopilot abort`.'
  );
}
